import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2, Mail, MapPin, PhoneCall, User, X } from 'lucide-react';
import { apiGet } from '../../lib/api.js';
import { fmtDate, fmtPhone } from '../../lib/format.js';
import { useLead } from '../../hooks/useLead.js';
import { useLocalTime } from '../../pages/leads/leadsConstants.jsx';
import NotesTab from '../leadDetail/tabs/NotesTab.jsx';
import SendPresetTextDialog from '../SendPresetTextDialog.jsx';
import LeadActionsMenu from './LeadActionsMenu.jsx';
import EditLeadModal from './EditLeadModal.jsx';
import LeadDetailsPane from '../leadDetail/LeadDetailsPane.jsx';

function Info({ label, children }) {
  if (children === undefined || children === null || children === '') return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="text-sm text-white/90 truncate">{children}</div>
    </div>
  );
}

/**
 * Lead detail drawer shown beside the softphone ("View Lead"). Shows contact
 * info, lead context, disposition tags, an always-editable note, and call
 * history for the focused lead, plus the purple actions menu. Everything is
 * plain REST + cache invalidation, so it never interferes with a live call.
 * @param {Object} props
 * @param {string|null} props.leadId - Focused lead (active call, power-dial lead, or last dialed).
 * @param {() => void} props.onClose
 * @param {'summary'|'full'} [props.mode] - Summary card, or every Lead Detail tab.
 * @param {(m: 'summary'|'full') => void} [props.onModeChange]
 * @returns {JSX.Element}
 */
export default function ViewLeadPanel({ leadId, onClose, mode = 'summary', onModeChange }) {
  const qc = useQueryClient();
  const notesRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);

  const { lead, calls, isLoading, error } = useLead(leadId, { refetchInterval: 15_000 });
  const localTime = useLocalTime(lead?.timezone);

  const { data: dispData } = useQuery({
    queryKey: ['dispositions'],
    queryFn: () => apiGet('/dispositions'),
    staleTime: 60_000,
    enabled: !!leadId,
  });
  const tags = (lead?.dispositionTags || [])
    .map((slug) => (dispData?.dispositions || []).find((d) => d.slug === slug))
    .filter(Boolean);

  function refresh() {
    ['lead', 'leads', 'dial-queue', 'phone-calls'].forEach((k) => qc.invalidateQueries({ queryKey: k === 'lead' ? ['lead', leadId] : [k] }));
  }

  const name = [lead?.firstName, lead?.lastName].filter(Boolean).join(' ');
  const place = [lead?.city, lead?.state, lead?.zip].filter(Boolean).join(', ');
  const vendor = lead?.vendor?.name || lead?.vendorName;
  const sold = Number(lead?.saleDetails?.annualPremium || lead?.annualPremium) > 0;

  return (
    <div className="h-full w-full flex flex-col bg-civix-panel border-l border-civix-border shadow-panel" data-testid="view-lead-panel">
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-civix-purple">
        <div className="min-w-0 flex items-center gap-2 text-white">
          <User className="w-4 h-4 shrink-0" />
          <span className="font-display font-bold truncate">{name || (leadId ? 'Lead' : 'View Lead')}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {leadId && (
            <Link to={`/leads/${leadId}`} className="text-white/80 hover:text-white" title="Open full lead page">
              <ExternalLink className="w-4 h-4" />
            </Link>
          )}
          <button onClick={onClose} className="text-white/80 hover:text-white" aria-label="Hide lead"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {leadId && onModeChange && (
        <div className="flex border-b border-civix-border bg-civix-panelLight text-xs font-semibold" role="tablist">
          {[['summary', 'Summary'], ['full', 'Full details']].map(([id, label]) => (
            <button key={id} role="tab" aria-selected={mode === id} onClick={() => onModeChange(id)}
              className={`flex-1 py-2 transition ${mode === id ? 'text-white border-b-2 border-civix-purple' : 'text-white/50 hover:text-white'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {leadId && mode === 'full' ? (
        <div className="flex-1 min-h-0">
          <LeadDetailsPane leadId={leadId} hideHeader />
        </div>
      ) : !leadId ? (
        <div className="flex-1 flex items-center justify-center p-8 text-center text-sm text-white/50">
          No lead selected. Select or dial a lead to view their information.
        </div>
      ) : isLoading && !lead ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-civix-purple" /></div>
      ) : !lead ? (
        <div className="flex-1 flex items-center justify-center p-8 text-center text-sm text-white/50">
          {error?.response?.status === 403
            ? 'You do not have access to this lead.'
            : 'This lead could not be loaded. It may have been deleted.'}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <LeadActionsMenu
              leadId={leadId}
              lead={lead}
              onEdit={() => setEditing(true)}
              onNotes={() => notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              onSms={() => setSmsOpen(true)}
              onChanged={refresh}
            />
            {lead.blocked && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 font-semibold">BLOCKED</span>}
            {sold && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold">SOLD</span>}
            {tags.map((d) => (
              <span key={d.slug} className="text-[10px] font-medium rounded-sm px-1.5 py-0.5 border"
                style={{ backgroundColor: `${d.color}25`, color: d.color, borderColor: `${d.color}50` }}>{d.label}</span>
            ))}
          </div>

          <section className="rounded-xl border border-civix-border bg-civix-panelLight p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Info label="Phone">
                <span className="inline-flex items-center gap-1"><PhoneCall className="w-3 h-3 text-civix-purpleSoft" />{fmtPhone(lead.phone)}</span>
              </Info>
              <Info label="Local time">{lead.timezone ? localTime : ''}</Info>
              <Info label="Email">
                {lead.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3 text-civix-purpleSoft" />{lead.email}</span> : ''}
              </Info>
              <Info label="Location">
                {place ? <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-civix-purpleSoft" />{place}</span> : ''}
              </Info>
              <Info label="Birthday">{lead.birthday ? fmtDate(lead.birthday).split(' ').slice(0, 3).join(' ') : ''}</Info>
              <Info label="Alt phone">{lead.altPhone ? fmtPhone(lead.altPhone) : ''}</Info>
            </div>
          </section>

          <section className="rounded-xl border border-civix-border bg-civix-panelLight p-3">
            <div className="text-[10px] uppercase tracking-wider text-civix-purpleSoft mb-2">Lead context</div>
            <div className="grid grid-cols-2 gap-3">
              <Info label="Vendor">{vendor}</Info>
              <Info label="Source">{lead.attribution?.sourceName || lead.source}</Info>
              <Info label="Product interest">{lead.productInterest}</Info>
              <Info label="Status">{lead.status}</Info>
              <Info label="Annual premium">{sold ? `$${Number(lead.saleDetails?.annualPremium || lead.annualPremium).toLocaleString()}` : ''}</Info>
              <Info label="Commission">{Number(lead.commission) > 0 ? `$${Number(lead.commission).toLocaleString()}` : ''}</Info>
              <Info label="Received">{lead.createdAt ? fmtDate(lead.createdAt) : ''}</Info>
              <Info label="Last disposition">{lead.dispositionSetAt ? fmtDate(lead.dispositionSetAt) : ''}</Info>
            </div>
          </section>

          <section ref={notesRef} className="rounded-xl border border-civix-border bg-civix-panelLight p-3">
            <NotesTab leadId={leadId} lead={lead} onChange={refresh} />
          </section>

          <section className="rounded-xl border border-civix-border bg-civix-panelLight p-3">
            <div className="text-[10px] uppercase tracking-wider text-civix-purpleSoft mb-2">Call history</div>
            {calls.length === 0 ? (
              <div className="text-xs text-white/40">No calls yet.</div>
            ) : (
              <ul className="space-y-2">
                {calls.slice(0, 8).map((c) => (
                  <li key={c._id} className="text-xs text-white/80 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="capitalize">{c.direction || 'call'} · {c.status || '—'}{c.duration ? ` · ${Math.ceil(c.duration / 60)}m` : ''}</span>
                      <span className="text-white/40">{c.createdAt ? fmtDate(c.createdAt) : ''}</span>
                    </div>
                    {c.disposition && <div className="text-white/50">Disposition: {String(c.disposition)}</div>}
                    {c.recordingUrl && <audio controls preload="none" src={c.recordingUrl} className="w-full h-8" />}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {editing && <EditLeadModal leadId={leadId} lead={lead} onClose={() => setEditing(false)} onSaved={refresh} />}
      {smsOpen && <SendPresetTextDialog leadId={leadId} onClose={() => setSmsOpen(false)} onSent={refresh} />}
    </div>
  );
}
